import {
  ContactDetails,
  CriticalNote,
  PractitionerPatientIdentityValues,
} from '@common/models'
import {
  calculateAgeFromDateOfBirth,
  formatDate,
  MISSING_ID,
} from '@common/utils'
import React, {useContext, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {DateTime} from 'luxon'
import {Link} from 'react-router-dom'

import {Avatar} from '~/components/general/avatar/Avatar'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {theme} from '~/styles/theme'
import {formatName} from '~/utils'
import {Icon} from '~/components/general/icon/Icon'
import {ContactLink} from '~/components/general/contactCard/ContactLink'
import {ContactCard} from '~/components/general/contactCard/ContactCard'
import {IconButton} from '~/components/general/iconButton/IconButton'
import {EditPatientIdentityModal} from '~/components/facility/screening/components/EditPatientIdentityModal'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {
  useGetPractitionerPatientIdentityCapability,
  useUpdatePractitionerPatientIdentityDemographics,
} from '~/api'

type PatientHeaderProps = {
  patient?: {
    dateOfBirth?: string
    gender?: PractitionerPatientIdentityValues['gender']
    providedDateOfBirth?: string
    firstName?: string
    secondName?: string
    patientId?: string
    phoneNumber?: string
  }
  criticalNote?: CriticalNote | null
  contactDetails?: ContactDetails
  showNameAsLink?: boolean
  patientIdentityEditConfig?: {
    facilityId: string
  }
}

export const PatientHeader: React.FC<PatientHeaderProps> = ({
  criticalNote,
  patient,
  contactDetails,
  showNameAsLink = true,
  patientIdentityEditConfig,
}) => {
  const {t} = useTranslation<string>()
  const {addNotification} = useContext(NotificationContext)
  const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false)
  const patientId = patient?.patientId ?? MISSING_ID
  const facilityId = patientIdentityEditConfig?.facilityId ?? MISSING_ID
  const hasPatientIdentityEdit = Boolean(
    patientIdentityEditConfig?.facilityId && patient?.patientId,
  )

  const patientName = formatName(patient)
  const criticalNoteContent = criticalNote?.content

  const patientAge = useMemo(
    () => calculateAgeFromDateOfBirth(patient?.dateOfBirth),
    [patient?.dateOfBirth],
  )

  const patientAgeText = t('{{count}} year old (DOB {{dateOfBirth}})', {
    count: patientAge,
    dateOfBirth: formatDate(DateTime.DATE_SHORT, patient?.dateOfBirth),
  })

  const unParseableDateOfBirthText = t('Date of Birth: {{dateOfBirth}}', {
    dateOfBirth: patient?.providedDateOfBirth,
  })

  const {data: patientIdentityCapability} =
    useGetPractitionerPatientIdentityCapability(
      {
        facilityId,
        patientId,
      },
      {
        enabled: hasPatientIdentityEdit,
      },
    )
  const canEditPatientDetails = Boolean(patientIdentityCapability?.canEdit)

  const {mutate: updatePatientIdentity, isPending: isUpdatingPatientIdentity} =
    useUpdatePractitionerPatientIdentityDemographics({
      facilityId,
      patientId,
    })

  const initialValues: PractitionerPatientIdentityValues = {
    dateOfBirth: patient?.dateOfBirth || null,
    gender: patient?.gender || null,
    phoneNumber: patient?.phoneNumber || null,
  }

  return (
    <>
      <Container flex align="center" spacing={3}>
        <Avatar size="lg" name={patient} patientId={patient?.patientId} />
        <div>
          <Container flex direction="row" align="center" spacing={2}>
            <Typography size="h3" weight="medium">
              {showNameAsLink ? (
                <Link to={`/app/patients/${patient?.patientId}/dashboard`}>
                  {patientName}
                </Link>
              ) : (
                patientName
              )}
            </Typography>
            {patientAge != null && (
              <Typography color={theme.colors.grey.main} flexShrink={0}>
                {patientAgeText}
              </Typography>
            )}
            {patientAge == null && patient?.providedDateOfBirth && (
              <Typography color={theme.colors.grey.main} flexShrink={0}>
                {unParseableDateOfBirthText}
              </Typography>
            )}
            {hasPatientIdentityEdit && (
              <IconButton
                name="Edit"
                onClick={() => setIsEditPatientModalOpen(true)}
                color="secondary"
                title={
                  canEditPatientDetails
                    ? t('Edit patient details')
                    : t('Editing unavailable')
                }
                aria-label={
                  canEditPatientDetails
                    ? t('Edit patient details')
                    : t('Editing unavailable')
                }
                style={{
                  opacity: canEditPatientDetails ? 1 : 0.45,
                  filter: canEditPatientDetails ? 'none' : 'grayscale(1)',
                }}
              />
            )}
          </Container>
          <Container flex align="center" spacing={1}>
            <Icon name="MonitoringNote" color={theme.colors.red} />
            <Typography weight="bold" color={theme.colors.red}>
              {criticalNoteContent || t('Key information: ')}
              {!criticalNoteContent && (
                <Typography inline>{t('No details to display')}</Typography>
              )}
            </Typography>
          </Container>
        </div>
        {contactDetails && (
          <ContactCard>
            <ContactLink
              contactType="email"
              contactDetail={contactDetails.email}
            />
            {contactDetails.phoneNumber && (
              <ContactLink
                contactType="phone"
                contactDetail={contactDetails.phoneNumber}
              />
            )}
            {/* <Typography>{contactDetails.email}</Typography>
          <Typography>{contactDetails.phoneNumber}</Typography> */}
          </ContactCard>
        )}
      </Container>
      {hasPatientIdentityEdit && (
        <EditPatientIdentityModal
          isVisible={isEditPatientModalOpen}
          onClose={() => setIsEditPatientModalOpen(false)}
          initialValues={initialValues}
          isSaving={isUpdatingPatientIdentity}
          canEdit={canEditPatientDetails}
          reasonCodes={patientIdentityCapability?.reasonCodes || []}
          onSave={(payload) =>
            updatePatientIdentity(payload, {
              onSuccess: () => {
                addNotification(
                  'Patient details updated',
                  NotificationType.Success,
                )
                setIsEditPatientModalOpen(false)
              },
              onError: () =>
                addNotification(
                  'Failed to update patient details',
                  NotificationType.Error,
                ),
            })
          }
        />
      )}
    </>
  )
}
